/**
 * Display formatting. British English, en-GB grouping, one place per convention.
 *
 * Two rules the whole interface depends on:
 *   - Money arrives as an INTEGER number of minor units. Nothing outside this
 *     file divides by 100; call `money()` instead.
 *   - Ratios arrive as FRACTIONS. Nothing outside this file multiplies by 100;
 *     call `percent()` instead.
 *
 * A null amount means the signed-in role may not see that figure. It renders as
 * an em dash, never as a zero, because zero cost and hidden cost are different
 * statements and only one of them is true.
 */
import type { Category, PipelineStage, QuoteStatus, Role, ServiceLine } from './types';

/** What a hidden or absent figure looks like. */
export const DASH = '—';

const groupers = new Map<string, Intl.NumberFormat>();

function grouper(minimumFractionDigits: number, maximumFractionDigits: number): Intl.NumberFormat {
  const key = `${minimumFractionDigits}:${maximumFractionDigits}`;
  let fmt = groupers.get(key);
  if (!fmt) {
    fmt = new Intl.NumberFormat('en-GB', {
      minimumFractionDigits,
      maximumFractionDigits,
      useGrouping: true,
    });
    groupers.set(key, fmt);
  }
  return fmt;
}

export interface MoneyOptions {
  /** Currency code shown in front of the figure. Defaults to HKD. */
  currency?: string;
  /** Show cents. Off by default — commercial figures are quoted whole. */
  decimals?: boolean;
  /** What to render for null. Defaults to an em dash. */
  blank?: string;
  /** Drop the currency code and return the bare number, e.g. inside a table column header that already says HKD. */
  bare?: boolean;
  /** Always show a leading + on positive amounts. Useful for differences. */
  signed?: boolean;
}

/**
 * Format minor units for display. `money(775000)` is "HKD 7,750".
 *
 * Null is not zero: it means the role may not see the figure, so it renders as
 * an em dash. Pass `blank` to say something else.
 */
export function money(minor: number | null | undefined, opts: MoneyOptions = {}): string {
  if (minor === null || minor === undefined || !Number.isFinite(minor)) {
    return opts.blank ?? DASH;
  }
  const decimals = opts.decimals === true;
  const major = minor / 100;
  const body = grouper(decimals ? 2 : 0, decimals ? 2 : 0).format(Math.abs(major));
  const sign = major < 0 ? '-' : opts.signed && major > 0 ? '+' : '';
  const prefix = opts.bare ? '' : `${opts.currency ?? 'HKD'} `;
  return `${sign}${prefix}${body}`;
}

/**
 * A short form for dashboard tiles and bar labels, where a nine-digit contract
 * value would break the layout. `moneyShort(29400000000)` is "HKD 294m".
 */
export function moneyShort(minor: number | null | undefined, opts: MoneyOptions = {}): string {
  if (minor === null || minor === undefined || !Number.isFinite(minor)) {
    return opts.blank ?? DASH;
  }
  const major = Math.abs(minor) / 100;
  const sign = minor < 0 ? '-' : opts.signed && minor > 0 ? '+' : '';
  const prefix = opts.bare ? '' : `${opts.currency ?? 'HKD'} `;
  if (major >= 1_000_000) return `${sign}${prefix}${grouper(0, 1).format(major / 1_000_000)}m`;
  if (major >= 10_000) return `${sign}${prefix}${grouper(0, 0).format(major / 1_000)}k`;
  return `${sign}${prefix}${grouper(0, 0).format(major)}`;
}

/**
 * Format a fraction as a percentage. `percent(0.4726)` is "47.3%".
 *
 * The symbol is used in figures and tables; use `percentWords()` in prose, where
 * British English wants "per cent".
 */
export function percent(fraction: number | null | undefined, dp = 1): string {
  if (fraction === null || fraction === undefined || !Number.isFinite(fraction)) return DASH;
  return `${grouper(dp, dp).format(fraction * 100)}%`;
}

/** The same figure spelled out, for sentences: "47.3 per cent". */
export function percentWords(fraction: number | null | undefined, dp = 1): string {
  if (fraction === null || fraction === undefined || !Number.isFinite(fraction)) return DASH;
  return `${grouper(dp, dp).format(fraction * 100)} per cent`;
}

/** A plain count with en-GB grouping. `number(12500)` is "12,500". */
export function number(value: number | null | undefined, dp = 0): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH;
  return grouper(dp, dp).format(value);
}

/** Days, hours and other engine quantities: trims a trailing ".0". */
export function quantity(value: number | null | undefined, dp = 2): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH;
  return grouper(0, dp).format(value);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** `dateLong('2026-09-19')` is "19 Sep 2026". */
export function dateLong(iso: string | null | undefined): string {
  const d = parse(iso);
  if (!d) return DASH;
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** `dateShort('2026-09-19')` is "19/09/2026". Day first, as Hong Kong writes it. */
export function dateShort(iso: string | null | undefined): string {
  const d = parse(iso);
  if (!d) return DASH;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/** `dateTime('2026-09-19T02:45:51Z')` is "19 Sep 2026, 10:45". */
export function dateTime(iso: string | null | undefined): string {
  const d = parse(iso);
  if (!d) return DASH;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${dateLong(iso)}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** "3 days ago", "in 2 weeks". Falls back to the long date beyond a year. */
export function relativeDate(iso: string | null | undefined, now: Date = new Date()): string {
  const d = parse(iso);
  if (!d) return DASH;
  const diffDays = Math.round((d.getTime() - now.getTime()) / 86_400_000);
  const abs = Math.abs(diffDays);
  if (abs === 0) return 'today';
  if (abs === 1) return diffDays < 0 ? 'yesterday' : 'tomorrow';
  if (abs < 14) return diffDays < 0 ? `${abs} days ago` : `in ${abs} days`;
  if (abs < 60) {
    const weeks = Math.round(abs / 7);
    return diffDays < 0 ? `${weeks} weeks ago` : `in ${weeks} weeks`;
  }
  if (abs < 365) {
    const months = Math.round(abs / 30);
    return diffDays < 0 ? `${months} months ago` : `in ${months} months`;
  }
  return dateLong(iso);
}

// ------------------------------------------------------------------ label maps

/** The eight pipeline stages, in order. */
export const STAGE_ORDER: PipelineStage[] = [
  'lead',
  'qualifying',
  'scoping',
  'quoted',
  'negotiating',
  'contracting',
  'closed_won',
  'closed_lost',
];

export const STAGE_LABELS: Record<PipelineStage, string> = {
  lead: 'Lead',
  qualifying: 'Qualifying',
  scoping: 'Scoping',
  quoted: 'Quoted',
  negotiating: 'Negotiating',
  contracting: 'Contracting',
  closed_won: 'Closed Won',
  closed_lost: 'Closed Lost',
};

/** What each stage means, straight from the pipeline configuration. */
export const STAGE_DEFINITIONS: Record<PipelineStage, string> = {
  lead: 'Enquiry received, not yet qualified',
  qualifying: 'Discovery in progress, scoping questions being answered',
  scoping: 'Category chosen, form being completed, engineer estimate requested',
  quoted: 'Quotation or proposal issued to the client',
  negotiating: 'Client has responded, revisions or clarifications in progress',
  contracting: 'Commercially agreed, contract or purchase order in flight',
  closed_won: 'Signed. Reference, start date and confirmed value recorded',
  closed_lost: 'Not proceeding. A loss reason is recorded against it',
};

export function stageLabel(key: string | null | undefined): string {
  if (!key) return DASH;
  return STAGE_LABELS[key as PipelineStage] ?? key;
}

/** The twelve governed categories, in routing order. */
export const CATEGORY_ORDER: Category[] = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  'M1',
  'M2',
  'M3',
  'M4',
  'DES',
];

export const CATEGORY_LABELS: Record<Category, string> = {
  '1': 'Pool of hours, under ten users',
  '2': 'Managed service retainer',
  '3': 'Small works',
  '4': 'Audits, assessments and due diligence',
  '5': 'Projects',
  '6': 'Multi-country and dedicated resource',
  '7': 'Regulated financial services',
  M1: 'Procurement',
  M2: 'Contract staffing',
  M3: 'Tenders and rate schedules',
  M4: 'Change to work already sold',
  DES: 'Design consultancy',
};

/** A short form for table cells and chips, where the full label will not fit. */
// From the domain package, which is where the API reads it from too. Two copies
// of the category names is one too many.
import { CATEGORY_SHORT } from '@pts/domain';
export { CATEGORY_SHORT };

export function categoryLabel(key: string | null | undefined): string {
  if (!key) return DASH;
  return CATEGORY_LABELS[key as Category] ?? key;
}

export function categoryShort(key: string | null | undefined): string {
  if (!key) return DASH;
  return CATEGORY_SHORT[key as Category] ?? key;
}

/** "Category 3 — Small works". How a category is named in a heading. */
export function categoryTitle(key: string | null | undefined): string {
  if (!key) return DASH;
  const numbered = /^[1-7]$/.test(key);
  return `${numbered ? `Category ${key}` : key} ${DASH} ${categoryShort(key)}`;
}

export const QUOTE_STATUS_LABELS: Record<QuoteStatus, string> = {
  draft: 'Draft',
  pending_approval: 'Pending approval',
  approved: 'Approved',
  issued: 'Issued',
  accepted: 'Accepted',
  superseded: 'Superseded',
  declined: 'Declined',
  withdrawn: 'Withdrawn',
};

export function quoteStatusLabel(key: string | null | undefined): string {
  if (!key) return DASH;
  return QUOTE_STATUS_LABELS[key as QuoteStatus] ?? key;
}

export const ROLE_LABELS: Record<Role, string> = {
  commercial_administrator: 'Commercial Administrator',
  commercial_user: 'Commercial User',
  engineer: 'Engineer',
  approver: 'Approver',
  read_only: 'Read Only',
};

export function roleLabel(key: string | null | undefined): string {
  if (!key) return DASH;
  return ROLE_LABELS[key as Role] ?? key;
}

export const SERVICE_LINE_LABELS: Record<ServiceLine, string> = {
  MSV: 'Managed services',
  PRJ: 'Projects',
  ADV: 'Advisory',
  DES: 'Design',
  PRC: 'Procurement',
  RES: 'Resourcing',
};

export function serviceLineLabel(key: string | null | undefined): string {
  if (!key) return DASH;
  return SERVICE_LINE_LABELS[key as ServiceLine] ?? key;
}

/** `titleCase('closed_won')` is "Closed won". For enum values with no label map. */
export function titleCase(key: string | null | undefined): string {
  if (!key) return DASH;
  const spaced = key.replace(/[_-]+/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Two-letter initials for an avatar. `initials('Ben Fox')` is "BF". */
export function initials(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

/** "3 quotes", "1 quote". Plurals without a library. */
export function pluralise(count: number, singular: string, plural?: string): string {
  return `${number(count)} ${count === 1 ? singular : (plural ?? `${singular}s`)}`;
}
